import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Select,
  Table,
  Tabs,
  useToast,
  type Column,
} from "./ui";
import {
  databaseService,
  type DbRowsResult,
  type DbTable,
  type ProjectDatabase,
} from "../services/database.service";

/**
 * Banco do PROJETO GERADO: schemas, tabelas, colunas, índices, chaves
 * estrangeiras, migrations e visualização paginada de registros.
 * Não há execução de SQL livre — migrations são a fonte oficial da estrutura.
 */
export default function DatabasePanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [data, setData] = useState<ProjectDatabase | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string>("");
  const [rows, setRows] = useState<DbRowsResult | null>(null);
  const [page, setPage] = useState(1);
  const poll = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await databaseService.get(projectId));
    } catch (error) {
      toast.error((error as Error).message);
    }
  }, [projectId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Enquanto o runner monta o retrato do banco, consulta de novo.
  useEffect(() => {
    if (data?.status !== "pending") return;
    const id = window.setTimeout(() => void load(), 3000);
    return () => window.clearTimeout(id);
  }, [data, load]);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      await databaseService.refresh(projectId);
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  }, [projectId, load, toast]);

  const loadRows = useCallback(
    async (reference: string, wanted: number) => {
      const [schema, table] = reference.split(".");
      if (!schema || !table) return;

      setRows({ status: "pending" });

      try {
        const { token } = await databaseService.requestRows(projectId, schema, table, wanted);

        const check = async (attempt: number) => {
          const result = await databaseService.rowsResult(projectId, token);
          if (result.status === "pending" && attempt < 20) {
            poll.current = window.setTimeout(() => void check(attempt + 1), 1500);
            return;
          }
          setRows(result);
        };

        await check(0);
      } catch (error) {
        setRows({ status: "failed", error: (error as Error).message });
      }
    },
    [projectId],
  );

  useEffect(
    () => () => {
      if (poll.current) window.clearTimeout(poll.current);
    },
    [],
  );

  const tableColumns = useMemo(
    () => (data?.columns ?? []).filter((column) => `${column.schema}.${column.table}` === selected),
    [data, selected],
  );
  const tableIndexes = useMemo(
    () => (data?.indexes ?? []).filter((index) => `${index.schema}.${index.table}` === selected),
    [data, selected],
  );
  const tableKeys = useMemo(
    () => (data?.foreign_keys ?? []).filter((key) => `${key.schema}.${key.table}` === selected),
    [data, selected],
  );

  const tableCols: Column<DbTable>[] = [
    {
      key: "name",
      header: "Tabela",
      render: (row) => (
        <button
          type="button"
          className="font-mono text-xs text-brand hover:underline"
          onClick={() => {
            setSelected(`${row.schema}.${row.name}`);
            setPage(1);
            setRows(null);
          }}
        >
          {row.schema}.{row.name}
        </button>
      ),
    },
    {
      key: "rows",
      header: "Registros (aprox.)",
      align: "right",
      render: (row) => <span className="text-xs">{row.approximate_rows ?? 0}</span>,
    },
    {
      key: "size",
      header: "Tamanho",
      align: "right",
      hideOnMobile: true,
      render: (row) => <span className="text-xs text-ink-muted">{row.size ?? "—"}</span>,
    },
  ];

  if (!data) return <p className="text-xs text-ink-muted">Carregando banco do projeto…</p>;

  if (!data.enabled) {
    return (
      <EmptyState
        title="Banco do projeto indisponível"
        description="Ligue o ambiente de execução isolado para inspecionar o banco de desenvolvimento deste projeto."
      />
    );
  }

  const total = rows?.total ?? 0;
  const perPage = rows?.per_page ?? data.rows_per_page;
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, perPage)));
  const rowKeys = rows?.rows?.[0] ? Object.keys(rows.rows[0]) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge tone={data.status === "ready" ? "ok" : data.status === "failed" ? "danger" : "warn"}>
            {data.status === "ready"
              ? "Atualizado"
              : data.status === "failed"
                ? "Falhou"
                : data.status === "pending"
                  ? "Lendo…"
                  : "Sem leitura"}
          </Badge>
          <span className="text-[11px] text-ink-muted">
            Banco de desenvolvimento deste projeto — separado do banco do Builder.
          </span>
        </div>
        <Button size="sm" onClick={() => void refresh()} disabled={busy}>
          Atualizar estrutura
        </Button>
      </div>

      {data.error && <p className="text-xs text-danger">{data.error}</p>}

      <Tabs
        items={[
          {
            id: "structure",
            label: "Estrutura",
            content: (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {data.schemas.map((schema) => (
                    <Badge key={schema.name} tone="info">
                      {schema.name} · {schema.tables} tabela(s)
                    </Badge>
                  ))}
                  {data.schemas.length === 0 && (
                    <span className="text-xs text-ink-muted">Nenhum schema lido ainda.</span>
                  )}
                </div>

                <Table
                  columns={tableCols}
                  rows={data.tables}
                  rowKey={(row) => `${row.schema}.${row.name}`}
                  empty={
                    <EmptyState
                      title="Nenhuma tabela"
                      description="Rode as migrations do projeto e atualize a estrutura."
                    />
                  }
                />

                {selected && (
                  <Card>
                    <h3 className="text-sm font-semibold">{selected}</h3>

                    <h4 className="mt-3 text-xs font-semibold uppercase text-ink-muted">Colunas</h4>
                    <ul className="mt-1 space-y-1 font-mono text-[11px] text-ink-muted">
                      {tableColumns.map((column) => (
                        <li key={column.name}>
                          {column.name} · {column.type}
                          {column.length ? `(${column.length})` : ""} ·{" "}
                          {column.nullable ? "nulo permitido" : "obrigatório"}
                          {column.default ? ` · padrão ${column.default}` : ""}
                        </li>
                      ))}
                      {tableColumns.length === 0 && <li>Sem colunas lidas.</li>}
                    </ul>

                    <h4 className="mt-3 text-xs font-semibold uppercase text-ink-muted">Índices</h4>
                    <ul className="mt-1 space-y-1 font-mono text-[11px] text-ink-muted">
                      {tableIndexes.map((index) => (
                        <li key={index.name}>
                          {index.name}
                          {index.unique ? " · único" : ""}
                        </li>
                      ))}
                      {tableIndexes.length === 0 && <li>Nenhum índice.</li>}
                    </ul>

                    <h4 className="mt-3 text-xs font-semibold uppercase text-ink-muted">
                      Chaves estrangeiras
                    </h4>
                    <ul className="mt-1 space-y-1 font-mono text-[11px] text-ink-muted">
                      {tableKeys.map((key) => (
                        <li key={key.name}>
                          {key.name} → {key.references_schema}.{key.references_table}
                        </li>
                      ))}
                      {tableKeys.length === 0 && <li>Nenhuma chave estrangeira.</li>}
                    </ul>
                  </Card>
                )}
              </div>
            ),
          },
          {
            id: "rows",
            label: "Registros",
            content: (
              <div className="space-y-3">
                <div className="flex flex-wrap items-end gap-2">
                  <Select
                    label="Tabela"
                    value={selected}
                    onChange={(event) => {
                      setSelected(event.target.value);
                      setPage(1);
                      setRows(null);
                    }}
                    options={[
                      { value: "", label: "Selecione uma tabela" },
                      ...data.tables.map((table) => ({
                        value: `${table.schema}.${table.name}`,
                        label: `${table.schema}.${table.name}`,
                      })),
                    ]}
                  />
                  <Button
                    size="sm"
                    disabled={!selected}
                    onClick={() => void loadRows(selected, page)}
                  >
                    Ver registros
                  </Button>
                </div>

                {rows?.status === "pending" && (
                  <p className="text-xs text-ink-muted">Consultando o banco do projeto…</p>
                )}
                {rows?.status === "failed" && <p className="text-xs text-danger">{rows.error}</p>}

                {rows?.status === "ready" && (
                  <div className="space-y-2">
                    <div className="-mx-5 overflow-x-auto px-5">
                      <table className="w-full min-w-[560px] border-collapse text-xs">
                        <thead>
                          <tr className="border-b border-line/60 text-left">
                            {rowKeys.map((key) => (
                              <th key={key} className="py-2 pr-4 font-mono text-[11px] text-ink-muted">
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(rows.rows ?? []).map((row, index) => (
                            <tr key={index} className="border-b border-line/30 last:border-0">
                              {rowKeys.map((key) => (
                                <td key={key} className="py-2 pr-4 font-mono text-[11px]">
                                  {row[key] === null || row[key] === undefined
                                    ? "—"
                                    : String(row[key])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-ink-muted">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={page <= 1}
                        onClick={() => {
                          const next = page - 1;
                          setPage(next);
                          void loadRows(selected, next);
                        }}
                      >
                        Anterior
                      </Button>
                      <span>
                        Página {rows.page ?? page} de {lastPage} · {total} registro(s)
                      </span>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={page >= lastPage}
                        onClick={() => {
                          const next = page + 1;
                          setPage(next);
                          void loadRows(selected, next);
                        }}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                )}

                {!data.sql_console && (
                  <p className="text-[11px] text-ink-muted">
                    Consultas livres em SQL não estão disponíveis: a estrutura é definida pelas
                    migrations do projeto.
                  </p>
                )}
              </div>
            ),
          },
          {
            id: "migrations",
            label: "Migrations",
            content: (
              <ul className="max-h-[55vh] space-y-1 overflow-y-auto font-mono text-[11px] text-ink-muted">
                {data.migrations.length === 0 && <li>Nenhuma migration criada ainda.</li>}
                {data.migrations.map((migration) => (
                  <li key={migration.path} className="flex items-center gap-2">
                    <Badge tone={migration.applied ? "ok" : "neutral"}>
                      {migration.applied ? "aplicada" : "pendente"}
                    </Badge>
                    <span className="truncate">{migration.name}</span>
                  </li>
                ))}
              </ul>
            ),
          },
        ]}
      />
    </div>
  );
}
